import { useEffect, type PropsWithChildren } from "react";
import { Navigate } from "react-router-dom";

import { LoadScreen } from "@/core/components/shared";
import { useSessionStore } from "@/core/stores/session.store";

import { isKeycloakAuthenticated, keycloakLogin } from "./keycloak";
import { isPasswordGrantMode } from "./password-grant";

/**
 * Garde de route : zone réservée aux utilisateurs authentifiés **et** ayant
 * validé le gate OTP post-login (`otpVerified` dans le session store).
 *
 * - Mode **password** (client confidentiel) : pas de session → `/login`
 *   (formulaire maison).
 * - Mode **sso** (client public) : pas de session → redirection vers la
 *   page de login hébergée Keycloak (`keycloakLogin`), il n'existe pas de
 *   formulaire de login maison dans ce mode (guide §3.1/§3.4).
 * - Session présente mais OTP non validé → `/verify-otp` (gate inséré
 *   avant la redirection vers `/app` après un login réussi).
 *
 * Prérequis : init terminée (garanti par `useKeycloakInit` dans App.tsx).
 */
export function RequireAuth({ children }: PropsWithChildren) {
  const accessToken = useSessionStore((state) => state.accessToken);
  const otpVerified = useSessionStore((state) => state.otpVerified);
  const authenticated = isPasswordGrantMode()
    ? Boolean(accessToken)
    : isKeycloakAuthenticated();

  useEffect(() => {
    if (!isPasswordGrantMode() && !authenticated) keycloakLogin();
  }, [authenticated]);

  if (!authenticated) {
    if (isPasswordGrantMode()) return <Navigate to="/login" replace />;
    // Redirection SSO en cours : loader plein écran (règle loaders).
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <LoadScreen />
      </div>
    );
  }

  // Gate OTP post-login : l'utilisateur a un jeton valide mais doit encore
  // valider un code OTP envoyé sur son e-mail avant d'accéder au dashboard.
  if (!otpVerified) return <Navigate to="/verify-otp" replace />;

  return <>{children}</>;
}
